// Ключ сессии исполнителя (сервер: worker_auth.js).
//
// Одного Google ID для входа больше мало: его знают заказчики (ссылки заданий SDK), офферволлы,
// партнёры. При входе сервер выдаёт ключ сессии (registerEmployer → auth_token), приложение
// хранит его рядом с Google ID и отправляет в заголовке X-Worker-Token со всеми запросами к API
// (обёртка над fetch ниже — ничего не нужно менять в местах, где идут запросы).
//
// Кто вошёл до обновления (Google ID в приложении есть, ключа нет), получает ключ через
// /api/worker/session: подтверждает вход токеном Google из адреса — SDK передаёт его при каждом
// открытии заданий. Если сервер отвечает, что ключ нужен (заголовок X-Worker-Auth), обёртка
// получает ключ заново и повторяет запрос; не вышло — onAuthFailed (войти заново).
import { getCookie, setCookie } from './CookieHelper';

const TOKEN_KEY = 'worker_token';
const TOKEN_GID_KEY = 'worker_token_gid';
const LAST_GID_KEY = 'last_google_user_id';
const API_BASE = 'https://app-coins.ru/api/';
const API_RE = /^https:\/\/(www\.)?(app-coins\.ru|upmob\.ru)\/api\//i;
const UPLOAD_KEY = 'worker_upload_token';
const SESSION_RE = /\/api\/worker\/session(\?|$)/i;

let urlGoogleToken = null;
let urlDeviceId = null;
let urlBundle = null;
let urlApiKey = null;
let uploadToken = null;
let origFetch = null;
let onAuthFailed = null;
let authFailedReported = false;
let sessionPromise = null;

const storageGet = (key) => {
    try { return getCookie(key); } catch (e) { return null; }
};
const storageSet = (key, value) => {
    try { setCookie(key, value); } catch (e) { /* хранилище недоступно — ключ живёт до перезагрузки */ }
};
const storageRemove = (key) => {
    try { localStorage.removeItem(key); } catch (e) { /* нет хранилища */ }
};
const present = (v) => typeof v === 'string' && v !== '' && v !== 'null' && v !== 'undefined';

// Параметр из «сырой» строки адреса — так же, как getUrlParameter (без смены кодировки остальных)
function rawParam(search, name) {
    const parts = search.replace(/^\?/, '').split('&');
    for (let i = 0; i < parts.length; i++) {
        const eq = parts[i].indexOf('=');
        const key = eq === -1 ? parts[i] : parts[i].slice(0, eq);
        if (key === name) {
            try { return eq === -1 ? '' : decodeURIComponent(parts[i].slice(eq + 1)); } catch (e) { return null; }
        }
    }
    return null;
}

// Значение из фрагмента адреса (#ключ=…) и фрагмент без него
function takeFragment(hash, name) {
    const re = new RegExp('([#&])' + name + '=([^&]*)');
    const m = re.exec(hash);
    if (!m) return { value: null, hash: hash };
    let value = null;
    try { value = decodeURIComponent(m[2]); } catch (e) { value = null; }
    const rest = hash.replace(re, '$1').replace(/&&+/g, '&').replace(/([#&])&/, '$1').replace(/[#&]$/, '');
    return { value: value, hash: rest };
}

// Данные из ключа (без проверки подписи — её делает сервер)
function tokenPayload(token) {
    try {
        let part = String(token).split('.')[1] || '';
        part = part.replace(/-/g, '+').replace(/_/g, '/');
        while (part.length % 4) part += '=';
        return JSON.parse(window.atob(part));
    } catch (e) {
        return null;
    }
}

// Токен Google из адреса (его передаёт SDK) запоминаем и убираем из адреса — до Метрики и
// до того, как адрес попадёт в историю. Самым первым его забирает скрипт в index.html (до
// скриптов капчи и счётчиков) — он оставляет токен в window.__waTokenGoogle.
// Во фрагменте адреса (на сервер он не уходит) приходят:
// #worker_token=… — ключ из админки («Открыть приложение исполнителя», на 2 часа). Принимается
//   только такой ключ: обычный ключ из чужой ссылки «вошёл» бы в чужой аккаунт;
// #upload_token=… — ключ страницы загрузки скриншотов (только отправить отчёт).
export function captureUrlAuth() {
    try {
        const loc = window.location;
        let search = loc.search || '';
        let hash = loc.hash || '';
        let changed = false;
        if (present(window.__waTokenGoogle)) urlGoogleToken = window.__waTokenGoogle;
        // в глобальной переменной токен видели бы и чужие скрипты на странице (капча, счётчики)
        try { delete window.__waTokenGoogle; } catch (e) { window.__waTokenGoogle = undefined; }
        const token = rawParam(search, 'token_google');
        if (present(token)) urlGoogleToken = token;
        const device = rawParam(search, 'device_id');
        if (present(device)) urlDeviceId = device;
        // из какого приложения открыто (для статистики сервера, если токен Google — от незнакомого client id)
        const bundle = rawParam(search, 'bundle');
        if (present(bundle)) urlBundle = bundle;
        const apiKey = rawParam(search, 'api_key');
        if (present(apiKey)) urlApiKey = apiKey;
        if (token !== null) {
            search = search.replace(/^\?/, '').split('&').filter((p) => p.split('=')[0] !== 'token_google').join('&');
            search = search ? '?' + search : '';
            changed = true;
        }
        const admin = takeFragment(hash, 'worker_token');
        if (admin.value !== null) {
            const gid = rawParam(loc.search || '', 'google_user_id');
            const p = tokenPayload(admin.value);
            if (present(gid) && present(admin.value) && p && p.a === 1 && p.g === gid) saveSession(admin.value, gid);
            hash = admin.hash;
            changed = true;
        }
        const upload = takeFragment(hash, 'upload_token');
        if (upload.value !== null) {
            if (present(upload.value)) {
                uploadToken = upload.value;
                // страницу могут перезагрузить — ключ живёт в этой вкладке, пока её не закроют
                try { sessionStorage.setItem(UPLOAD_KEY, upload.value); } catch (e) { /* без хранилища */ }
            }
            hash = upload.hash;
            changed = true;
        }
        if (!uploadToken) {
            try { uploadToken = sessionStorage.getItem(UPLOAD_KEY) || null; } catch (e) { uploadToken = null; }
        }
        if (changed) window.history.replaceState(window.history.state, '', loc.pathname + search + hash);
    } catch (e) { /* адрес не меняем */ }
}

export function getUrlGoogleToken() {
    return urlGoogleToken;
}

// Ключ страницы загрузки скриншотов (из #upload_token) — в хранилище не попадает
export function getUploadToken() {
    return uploadToken;
}

// Ключ сессии — только если он выдан тому же исполнителю, что сейчас в приложении
export function getSessionToken(gid) {
    const token = storageGet(TOKEN_KEY);
    if (!present(token)) return null;
    if (gid !== undefined && storageGet(TOKEN_GID_KEY) !== gid) return null;
    return token;
}

export function saveSession(token, gid) {
    if (!present(token) || !present(gid)) return;
    storageSet(TOKEN_KEY, token);
    storageSet(TOKEN_GID_KEY, gid);
    storageSet(LAST_GID_KEY, gid);
}

export function clearSession() {
    storageRemove(TOKEN_KEY);
    storageRemove(TOKEN_GID_KEY);
}

// Забыть вошедшего исполнителя (вход другим аккаунтом, войти заново на сайте)
export function forgetWorker() {
    const gid = storageGet('google_user_id');
    if (present(gid)) storageSet(LAST_GID_KEY, gid);
    storageRemove('google_user_id');
    clearSession();
}

export function lastWorkerGid() {
    const gid = storageGet(LAST_GID_KEY);
    return present(gid) ? gid : null;
}

// sub из токена Google (без проверки подписи — её делает сервер): чей это аккаунт
export function googleTokenSub(token) {
    try {
        if (!present(token)) return null;
        let part = token.split('.')[1] || '';
        part = part.replace(/-/g, '+').replace(/_/g, '/');
        while (part.length % 4) part += '=';
        const sub = JSON.parse(window.atob(part)).sub;
        return typeof sub === 'string' && sub ? sub : null;
    } catch (e) {
        return null;
    }
}

function formBody(params) {
    return Object.keys(params)
        .filter((k) => params[k] !== undefined && params[k] !== null)
        .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
        .join('&');
}

function rawFetch(url, init) {
    return (origFetch || window.fetch.bind(window))(url, init);
}

// Ключ по подтверждению: токен Google (свежий или недавний) или, для iPhone, device_id
// → { ok } | { ok: false, legacy, other, reason } — сервер точно отказал (-401 / -404 / -409)
//          | { ok: false, network } — нет связи или непонятный ответ (старый сервер, лимит попыток):
//            продолжаем как раньше, решит сервер на следующем запросе
export function requestSession(gid, proof) {
    const body = Object.assign({ google_user_id: gid }, proof || {});
    return rawFetch(API_BASE + 'worker/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: formBody(body)
    }).then((r) => r.json()).then((data) => {
        if (data && data.response == 1 && present(data.auth_token)) {    // eslint-disable-line eqeqeq
            saveSession(data.auth_token, gid);
            return { ok: true };
        }
        const code = data ? Number(data.response) : 0;
        if (code !== -401 && code !== -404 && code !== -409) return { ok: false, network: true };
        return {
            ok: false,
            legacy: !!data.legacy,
            other: !!data.other,
            reason: data.reason || String(code)
        };
    }, () => ({ ok: false, network: true }));
}

// Ключ для исполнителя из приложения: уже есть — сразу; нет — по токену Google из адреса
export function ensureSession(gid) {
    if (!present(gid)) return Promise.resolve({ ok: false });
    if (getSessionToken(gid)) return Promise.resolve({ ok: true });
    if (sessionPromise) return sessionPromise;
    const proof = {};
    if (urlGoogleToken) proof.token_google = urlGoogleToken;
    const device = urlDeviceId || storageGet('device_id');
    if (present(device)) proof.device_id = device;
    const bundle = urlBundle || storageGet('bundle');
    if (present(bundle)) proof.bundle = bundle;
    const apiKey = urlApiKey || storageGet('api_key');
    if (present(apiKey)) proof.api_key = apiKey;
    sessionPromise = requestSession(gid, proof).then((r) => {
        sessionPromise = null;
        return r;
    });
    return sessionPromise;
}

function hasTokenHeader(headers) {
    if (!headers) return false;
    if (typeof Headers !== 'undefined' && headers instanceof Headers) return headers.has('X-Worker-Token');
    return Object.keys(headers).some((k) => k.toLowerCase() === 'x-worker-token');
}

function withToken(init) {
    const gid = storageGet('google_user_id');
    const token = getSessionToken(gid);
    if (!token || hasTokenHeader(init && init.headers)) return init;
    const next = Object.assign({}, init || {});
    if (typeof Headers !== 'undefined' && next.headers instanceof Headers) {
        const copy = new Headers(next.headers);
        copy.set('X-Worker-Token', token);
        next.headers = copy;
    } else {
        next.headers = Object.assign({}, next.headers || {}, { 'X-Worker-Token': token });
    }
    return next;
}

// Решает обработчик (index.js): в переходный период (legacy) приложение продолжает без ключа,
// а сайт предлагает войти заново через Google — без этого не вывести деньги и не открыть чаты
function reportAuthFailed(result) {
    if (typeof onAuthFailed !== 'function') return;
    if (!(result && result.legacy)) {
        if (authFailedReported) return;                // «войти заново» — один раз
        authFailedReported = true;
    }
    try { onAuthFailed(result || {}); } catch (e) { /* обработчик экрана */ }
}

function sendWithSession(input, init, retry) {
    const sent = storageGet(TOKEN_KEY);
    return rawFetch(input, withToken(init)).then((res) => {
        let auth = null;
        try {
            const fresh = res.headers.get('X-Worker-Token');
            const gid = storageGet('google_user_id');
            if (fresh && present(gid)) saveSession(fresh, gid);       // сервер продлил ключ
            auth = res.headers.get('X-Worker-Auth');
        } catch (e) { auth = null; }
        if (!auth || !retry || hasTokenHeader(init && init.headers)) return res;
        // ключ не принят — забыть его (если параллельный запрос ещё не получил новый)
        if (auth === 'invalid' && storageGet(TOKEN_KEY) === sent) clearSession();
        return ensureSession(storageGet('google_user_id')).then((s) => {
            if (s.ok) return sendWithSession(input, init, false);
            if (!s.network) reportAuthFailed(s);
            return res;
        });
    });
}

// Подключается один раз при старте, до первых запросов (index.js)
export function installSessionFetch(handler) {
    onAuthFailed = handler;
    if (origFetch || typeof window.fetch !== 'function') return;
    origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
        if (typeof input !== 'string' || !API_RE.test(input) || SESSION_RE.test(input)) return origFetch(input, init);
        return sendWithSession(input, init, true);
    };
}

// Сменить обработчик «войти не удалось» (экран приложения подключается позже обёртки)
export function setAuthFailedHandler(handler) {
    onAuthFailed = handler;
}

// Ключ для страницы загрузки скриншотов (она открывается во внешнем браузере): на 2 часа и
// только для отправки отчёта. Нет ключа — пустая строка (страница отправит отчёт, как раньше).
export function linkTokenFor(gid) {
    if (!getSessionToken(gid)) return Promise.resolve('');
    return window.fetch(API_BASE + 'worker/linkToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: formBody({ google_user_id: gid })
    }).then((r) => r.json()).then((d) => (d && d.response == 1 && present(d.link_token) ? d.link_token : ''), () => '');   // eslint-disable-line eqeqeq
}
